import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trainingMinuteService, employeeService, productService, productRevisionService, machineService, attachmentService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ArrowLeft, Pencil, Save, X, Plus, Trash2, Upload, FileText, Download } from 'lucide-react';

const STATUS_MAP = {
  pianificato: { label: 'Pianificato', cls: 'bg-blue-100 text-blue-700' },
  svolto: { label: 'Svolto', cls: 'bg-green-100 text-green-700' },
  archiviato: { label: 'Archiviato', cls: 'bg-slate-200 text-slate-600' },
};
const RESULT_OPTS = [
  { value: '', label: '-' },
  { value: 'superata', label: 'Superata' },
  { value: 'non_superata', label: 'Non superata' },
  { value: 'da_verificare', label: 'Da verificare' },
];
const QUAL_OPTS = [
  { value: '', label: '-' },
  { value: 'idoneo', label: 'Idoneo' },
  { value: 'non_idoneo', label: 'Non idoneo' },
  { value: 'idoneo_con_affiancamento', label: 'Idoneo con affiancamento' },
];
const RESULT_CLS = { superata: 'bg-green-100 text-green-700', non_superata: 'bg-red-100 text-red-700', da_verificare: 'bg-amber-100 text-amber-700' };
const QUAL_CLS = { idoneo: 'bg-green-100 text-green-700', non_idoneo: 'bg-red-100 text-red-700', idoneo_con_affiancamento: 'bg-amber-100 text-amber-700' };
const ATTACH_CATS = [
  { value: 'verbale_firmato', label: 'Verbale firmato' },
  { value: 'slides', label: 'Slides' },
  { value: 'test', label: 'Documenti test' },
  { value: 'istruzioni', label: 'Istruzioni' },
  { value: 'altro', label: 'Altro' },
];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';
const fmtSize = (b) => b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;

export default function VerbaleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ['admin', 'quality', 'management'].includes(user?.role);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [minute, setMinute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  const [editingParticipants, setEditingParticipants] = useState(false);
  const [participantsForm, setParticipantsForm] = useState([]);
  const [savingParticipants, setSavingParticipants] = useState(false);

  // Lookups
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [revisions, setRevisions] = useState([]);
  const [machines, setMachines] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadMinute = useCallback(async () => {
    try {
      setLoading(true);
      const res = await trainingMinuteService.getById(id);
      setMinute(res.data);
    } catch { toast.error('Errore caricamento verbale'); navigate('/training-minutes'); } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadMinute(); }, [loadMinute]);
  useEffect(() => {
    Promise.all([employeeService.getAll(), productService.getAll(), machineService.getAll()])
      .then(([e, p, m]) => { setEmployees(e.data); setProducts(p.data); setMachines(m.data); }).catch(() => {});
  }, []);

  const loadRevisions = async (pid) => {
    if (!pid) { setRevisions([]); return; }
    try { const r = await productRevisionService.getAll({ product_id: pid }); setRevisions(r.data); } catch { setRevisions([]); }
  };

  // Info editing
  const startEditInfo = () => {
    setInfoForm({
      training_date: minute.training_date || '', start_time: minute.start_time || '',
      end_time: minute.end_time || '', duration_hours: minute.duration_hours || '',
      topic: minute.topic || '', description: minute.description || '',
      trainer: minute.trainer || '', location: minute.location || '',
      product_id: minute.product_id || '', revision_id: minute.revision_id || '',
      machine_id: minute.machine_id || '', status: minute.status || 'pianificato',
    });
    if (minute.product_id) loadRevisions(minute.product_id);
    setEditingInfo(true);
  };
  const saveInfo = async () => {
    try {
      const payload = { ...infoForm, duration_hours: parseFloat(infoForm.duration_hours) };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.duration_hours = parseFloat(infoForm.duration_hours);
      await trainingMinuteService.update(id, payload);
      toast.success('Dati aggiornati');
      setEditingInfo(false);
      loadMinute();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  // Participants
  const startEditParticipants = () => {
    setParticipantsForm((minute.participants || []).map(p => ({ ...p })));
    setEditingParticipants(true);
  };
  const addParticipant = (empId) => {
    if (participantsForm.some(p => p.employee_id === empId)) return;
    const emp = employees.find(e => e.id === empId);
    setParticipantsForm(f => [...f, {
      id: crypto.randomUUID(), employee_id: empId, presence: true,
      result: null, qualification: null, notes: '',
      employee_code: emp?.employee_code, first_name: emp?.first_name, last_name: emp?.last_name,
    }]);
  };
  const removeParticipant = (idx) => setParticipantsForm(f => f.filter((_, i) => i !== idx));
  const updateParticipant = (idx, field, value) => {
    setParticipantsForm(f => f.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };
  const saveParticipants = async () => {
    try {
      setSavingParticipants(true);
      const payload = participantsForm.map(p => ({
        id: p.id, employee_id: p.employee_id, presence: p.presence,
        result: p.result || null, qualification: p.qualification || null, notes: p.notes || null,
      }));
      await trainingMinuteService.updateParticipants(id, payload);
      toast.success('Partecipanti aggiornati');
      setEditingParticipants(false);
      loadMinute();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); } finally { setSavingParticipants(false); }
  };

  // Attachments
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const category = prompt('Categoria allegato:\n1. Verbale firmato\n2. Slides\n3. Documenti test\n4. Istruzioni\n5. Altro');
    const catMap = { '1': 'verbale_firmato', '2': 'slides', '3': 'test', '4': 'istruzioni', '5': 'altro' };
    const cat = catMap[category] || 'altro';
    try {
      setUploading(true);
      await attachmentService.upload(file, 'training_minute', id, cat, '');
      toast.success('Allegato caricato');
      loadMinute();
    } catch (err) { toast.error('Errore upload'); } finally { setUploading(false); e.target.value = ''; }
  };
  const deleteAttachment = async (attId) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    try { await attachmentService.delete(attId); toast.success('Allegato eliminato'); loadMinute(); } catch { toast.error('Errore'); }
  };

  const calcDuration = (start, end) => {
    if (!start || !end) return '';
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    return (eh * 60 + em - sh * 60 - sm) / 60;
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Caricamento...</div>;
  if (!minute) return null;

  const existingParticipantIds = (minute.participants || []).map(p => p.employee_id);
  const availableEmployees = employees.filter(e => e.status === 'attivo' && !participantsForm.some(p => p.employee_id === e.id));

  return (
    <div className="space-y-6" data-testid="verbale-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/training-minutes')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="verbale-detail-title">Verbale Formazione</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="font-mono text-sm text-slate-500">{minute.minute_code}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_MAP[minute.status]?.cls || ''}`}>{STATUS_MAP[minute.status]?.label || minute.status}</span>
          </div>
        </div>
      </div>

      {/* Section 1: Dati generali */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="section-dati-generali">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Dati generali</h2>
          {canEdit && !editingInfo && <Button size="sm" variant="outline" onClick={startEditInfo} data-testid="edit-info-btn"><Pencil className="w-4 h-4 mr-1" />Modifica</Button>}
          {editingInfo && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditingInfo(false)}>Annulla</Button>
              <Button size="sm" onClick={saveInfo} data-testid="save-info-btn"><Save className="w-4 h-4 mr-1" />Salva</Button>
            </div>
          )}
        </div>
        <div className="p-6">
          {editingInfo ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Data formazione</label>
                  <input type="date" value={infoForm.training_date} onChange={e => setInfoForm(f => ({ ...f, training_date: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Ora inizio</label>
                  <input type="time" value={infoForm.start_time} onChange={e => { const v = e.target.value; setInfoForm(f => { const d = calcDuration(v, f.end_time); return { ...f, start_time: v, ...(d > 0 ? { duration_hours: d.toFixed(1) } : {}) }; }); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Ora fine</label>
                  <input type="time" value={infoForm.end_time} onChange={e => { const v = e.target.value; setInfoForm(f => { const d = calcDuration(f.start_time, v); return { ...f, end_time: v, ...(d > 0 ? { duration_hours: d.toFixed(1) } : {}) }; }); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Durata ore</label>
                  <input type="number" step="0.5" min="0.5" value={infoForm.duration_hours} onChange={e => setInfoForm(f => ({ ...f, duration_hours: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Tema formazione</label>
                  <input type="text" value={infoForm.topic} onChange={e => setInfoForm(f => ({ ...f, topic: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Formatore</label>
                  <input type="text" value={infoForm.trainer} onChange={e => setInfoForm(f => ({ ...f, trainer: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Luogo formazione</label>
                  <input type="text" value={infoForm.location} onChange={e => setInfoForm(f => ({ ...f, location: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
                <div><label className="block text-xs font-medium text-slate-500 mb-1">Stato</label>
                  <select value={infoForm.status} onChange={e => setInfoForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg">
                    {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Descrizione</label>
                <textarea value={infoForm.description} onChange={e => setInfoForm(f => ({ ...f, description: e.target.value }))} rows={3} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-4 gap-x-6">
              <InfoField label="Codice verbale" value={minute.minute_code} />
              <InfoField label="Data formazione" value={fmtDate(minute.training_date)} />
              <InfoField label="Ora inizio" value={minute.start_time || '-'} />
              <InfoField label="Ora fine" value={minute.end_time || '-'} />
              <InfoField label="Durata ore" value={`${minute.duration_hours} ore`} />
              <InfoField label="Tema formazione" value={minute.topic} />
              <InfoField label="Formatore" value={minute.trainer} />
              <InfoField label="Luogo formazione" value={minute.location} />
              <InfoField label="Stato" value={<span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_MAP[minute.status]?.cls || ''}`}>{STATUS_MAP[minute.status]?.label}</span>} />
              {minute.description && <div className="col-span-2 md:col-span-4"><InfoField label="Descrizione" value={minute.description} /></div>}
            </div>
          )}
        </div>
      </div>

      {/* Section 2: Collegamenti tecnici */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="section-collegamenti">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Collegamenti tecnici</h2>
          {canEdit && !editingInfo && <Button size="sm" variant="outline" onClick={startEditInfo}><Pencil className="w-4 h-4 mr-1" />Modifica</Button>}
        </div>
        <div className="p-6">
          {editingInfo ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Prodotto</label>
                <select value={infoForm.product_id} onChange={e => { setInfoForm(f => ({ ...f, product_id: e.target.value, revision_id: '' })); loadRevisions(e.target.value); }} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg">
                  <option value="">Nessuno</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
                </select></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Revisione</label>
                <select value={infoForm.revision_id} onChange={e => setInfoForm(f => ({ ...f, revision_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" disabled={!infoForm.product_id}>
                  <option value="">Nessuna</option>
                  {revisions.map(r => <option key={r.id} value={r.id}>{r.revision_code}</option>)}
                </select></div>
              <div><label className="block text-xs font-medium text-slate-500 mb-1">Macchina</label>
                <select value={infoForm.machine_id} onChange={e => setInfoForm(f => ({ ...f, machine_id: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg">
                  <option value="">Nessuna</option>
                  {machines.map(m => <option key={m.id} value={m.id}>{m.machine_code} - {m.machine_name}</option>)}
                </select></div>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6">
              <InfoField label="Prodotto" value={minute.product_code ? `${minute.product_code} - ${minute.product_name || ''}` : '-'} />
              <InfoField label="Revisione prodotto" value={minute.revision_code || '-'} />
              <InfoField label="Macchina" value={minute.machine_code ? `${minute.machine_code} - ${minute.machine_name || ''}` : '-'} />
            </div>
          )}
        </div>
      </div>

      {/* Section 3: Partecipanti */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="section-partecipanti">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Partecipanti ({(minute.participants || []).length})</h2>
          {canEdit && !editingParticipants && <Button size="sm" variant="outline" onClick={startEditParticipants} data-testid="edit-participants-btn"><Pencil className="w-4 h-4 mr-1" />Modifica</Button>}
          {editingParticipants && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setEditingParticipants(false)}>Annulla</Button>
              <Button size="sm" onClick={saveParticipants} disabled={savingParticipants} data-testid="save-participants-btn"><Save className="w-4 h-4 mr-1" />{savingParticipants ? 'Salvataggio...' : 'Salva'}</Button>
            </div>
          )}
        </div>
        {editingParticipants ? (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 mb-3">
              <select onChange={e => { if (e.target.value) { addParticipant(e.target.value); e.target.value = ''; } }} className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="add-participant-select">
                <option value="">Aggiungi dipendente...</option>
                {availableEmployees.map(emp => <option key={emp.id} value={emp.id}>{emp.employee_code} - {emp.last_name} {emp.first_name}</option>)}
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-3 py-2">Dipendente</th><th className="px-3 py-2 w-20">Presenza</th>
                  <th className="px-3 py-2">Esito</th><th className="px-3 py-2">Idoneit&agrave;</th>
                  <th className="px-3 py-2">Note</th><th className="px-3 py-2 w-10"></th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {participantsForm.map((p, idx) => (
                    <tr key={p.id || idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-900">{p.last_name || ''} {p.first_name || ''} <span className="text-xs text-slate-400 font-mono">({p.employee_code || ''})</span></td>
                      <td className="px-3 py-2"><input type="checkbox" checked={p.presence} onChange={e => updateParticipant(idx, 'presence', e.target.checked)} className="rounded border-slate-300" /></td>
                      <td className="px-3 py-2"><select value={p.result || ''} onChange={e => updateParticipant(idx, 'result', e.target.value || null)} className="w-full px-2 py-1 text-sm border border-slate-200 rounded">
                        {RESULT_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select></td>
                      <td className="px-3 py-2"><select value={p.qualification || ''} onChange={e => updateParticipant(idx, 'qualification', e.target.value || null)} className="w-full px-2 py-1 text-sm border border-slate-200 rounded">
                        {QUAL_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select></td>
                      <td className="px-3 py-2"><input type="text" value={p.notes || ''} onChange={e => updateParticipant(idx, 'notes', e.target.value)} className="w-full px-2 py-1 text-sm border border-slate-200 rounded" placeholder="Note..." /></td>
                      <td className="px-3 py-2"><button onClick={() => removeParticipant(idx)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {minute.product_id && <p className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2 mt-2">Quando salvi con Esito = "Superata" e Idoneit&agrave; = "Idoneo", il sistema aggiorna automaticamente le competenze prodotto del dipendente.</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            {(minute.participants || []).length === 0 ? (
              <div className="p-6 text-center text-slate-400 text-sm">Nessun partecipante registrato</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-3">Dipendente</th><th className="px-4 py-3">Presenza</th>
                  <th className="px-4 py-3">Esito</th><th className="px-4 py-3">Idoneit&agrave;</th>
                  <th className="px-4 py-3">Note</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(minute.participants || []).map(p => (
                    <tr key={p.id} className="hover:bg-slate-50" data-testid={`participant-row-${p.employee_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{p.last_name} {p.first_name}</div>
                        <div className="text-xs text-slate-400 font-mono">{p.employee_code} {p.department ? `- ${p.department}` : ''}</div>
                      </td>
                      <td className="px-4 py-3">{p.presence ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Presente</span> : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700">Assente</span>}</td>
                      <td className="px-4 py-3">{p.result ? <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${RESULT_CLS[p.result] || 'bg-slate-100 text-slate-600'}`}>{RESULT_OPTS.find(o => o.value === p.result)?.label}</span> : '-'}</td>
                      <td className="px-4 py-3">{p.qualification ? <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${QUAL_CLS[p.qualification] || 'bg-slate-100 text-slate-600'}`}>{QUAL_OPTS.find(o => o.value === p.qualification)?.label}</span> : '-'}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{p.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Section 4: Allegati */}
      <div className="bg-white border border-slate-200 rounded-xl" data-testid="section-allegati">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Allegati ({(minute.attachments || []).length})</h2>
          {canEdit && (
            <label className="cursor-pointer">
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="upload-input" />
              <Button size="sm" type="button" disabled={uploading} asChild><span><Upload className="w-4 h-4 mr-1" />{uploading ? 'Caricamento...' : 'Carica allegato'}</span></Button>
            </label>
          )}
        </div>
        <div className="p-4">
          {(minute.attachments || []).length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm">Nessun allegato. Carica verbale firmato, slides, test o istruzioni.</div>
          ) : (
            <div className="space-y-2">
              {(minute.attachments || []).map(a => (
                <div key={a.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`attachment-${a.id}`}>
                  <FileText className="w-8 h-8 text-slate-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{a.file_name}</p>
                    <p className="text-xs text-slate-400">
                      {ATTACH_CATS.find(c => c.value === a.category)?.label || a.category} &middot; {fmtSize(a.file_size || 0)} &middot; {fmtDate(a.created_at)}{a.uploaded_by_name ? ` &middot; ${a.uploaded_by_name}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={`${API_URL}${a.file_url}`} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-white" title="Scarica"><Download className="w-4 h-4" /></a>
                    {canEdit && <button onClick={() => deleteAttachment(a.id)} className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg hover:bg-white" title="Elimina"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="text-sm text-slate-900 mt-0.5">{value || '-'}</p>
    </div>
  );
}
